"use client";

import * as React from "react";
import Image from "next/image";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Check } from "lucide-react";
import { getAttendanceCounts } from "@/lib/server-actions/attendanceRecordActions";
import { getScopeImage } from "@/lib/postImageUtils";
import {
  buildTrailStations,
  getTrailCategoryOptions,
  getTrailCategoryPath,
  getTrailHref,
  TRAIL_QUERY_PARAM,
  type TrailCategoryOption,
  type TrailStationDef,
} from "@/lib/article-trail";
import { lastContinuousAttendedIndex } from "@/lib/trail-map-progress";
import { useArticleShellSlots } from "./ArticleShellContext";
import SeasonalImage from "@/components/SeasonalImage";
import { cn } from "@/lib/utils";

/**
 * 한길 지도 — 오솔길(trail) 비주얼 (JCG-2061).
 *
 * 지역 = MDX 글 페이지. 카테고리 글들이 지그재그 흙길로 이어지고, 감자(정 로고)가
 * 현재 지역에 서서 걷는다. 지역 클릭 시 해당 페이지로 이동.
 *
 * - 길 순서: `@/lib/article-trail` (subject-posts SSOT 파생)
 * - 지역 status: 출석 기록(`getAttendanceCounts`, 활성 회차, reviewCount>0) → done, 현재 경로 → current,
 *   그 외 첫 미완료 → next, 나머지 → locked. locked 도 클릭 가능(시각 표시용).
 * - 각 지역은 회독 수(reviewCount)를 함께 들고 있어 노드 라벨에 "n회독" 뱃지를 표기한다(JCG-2348).
 */

const WALKER = "/logo.webp";

type StationStatus = "done" | "current" | "next" | "locked";

interface TrailStation extends TrailStationDef {
  status: StationStatus;
  /** 출석 완료 여부 — current 정거장도 출석하면 true(체크 표시용) */
  attended: boolean;
  /** 회독 수(이 페이지를 몇 번 공부했는지). 미출석이면 0. (JCG-2348) */
  reviewCount: number;
}

/* ── 컨테이너 폭 측정 (지그재그 좌표 계산용) ── */
function useMeasure(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [w, setW] = React.useState(268);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setW(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/* ── 출석/경로 기반 지역 status 계산 ── */
function useTrailStations(
  pathname: string,
  requestedCategoryPath?: string | null,
): {
  stations: TrailStation[];
  ready: boolean;
  categoryPath: string | null;
} {
  const categoryPath = React.useMemo(
    () => getTrailCategoryPath(pathname, requestedCategoryPath),
    [pathname, requestedCategoryPath],
  );
  const defs = React.useMemo(
    () => (categoryPath ? buildTrailStations(categoryPath) : []),
    [categoryPath],
  );

  // slug(앞 슬래시 없음) → 회독 수. 출석한 페이지만 키로 존재(reviewCount>=1). (JCG-2348)
  const [countMap, setCountMap] = React.useState<Map<string, number> | null>(
    null,
  );

  // 출석 토글/회독 +1 시 셸이 attendanceVersion 을 올리면(refreshAttendance) 회독 수를 재조회한다.
  const { attendanceVersion } = useArticleShellSlots();

  React.useEffect(() => {
    let alive = true;
    getAttendanceCounts()
      .then((counts) => {
        // js-index-maps: slug→reviewCount Map 으로 변환해 station 계산을 O(1) 조회로.
        if (alive) setCountMap(new Map(counts.map((c) => [c.slug, c.reviewCount])));
      })
      .catch(() => {
        if (alive) setCountMap(new Map());
      });
    return () => {
      alive = false;
    };
  }, [attendanceVersion]);

  const stations = React.useMemo<TrailStation[]>(() => {
    const currentPath = pathname.replace(/\/+$/, "") || "/";
    const counts = countMap ?? new Map<string, number>();

    const base: TrailStation[] = defs.map((d) => {
      const reviewCount = counts.get(d.href.slice(1)) ?? 0;
      const isAttended = reviewCount > 0;
      if (d.href === currentPath)
        return { ...d, status: "current", attended: isAttended, reviewCount };
      if (isAttended) return { ...d, status: "done", attended: true, reviewCount };
      return { ...d, status: "locked", attended: false, reviewCount };
    });

    // next = current(없으면 처음) 이후 첫 미완료 지역 하나
    const currentIdx = base.findIndex((s) => s.status === "current");
    const from = currentIdx >= 0 ? currentIdx + 1 : 0;
    for (let i = from; i < base.length; i++) {
      if (base[i].status === "locked") {
        base[i] = { ...base[i], status: "next" };
        break;
      }
    }
    return base;
  }, [defs, countMap, pathname]);

  return { stations, ready: countMap !== null, categoryPath };
}

/* ── 지그재그 좌표 ── */
const PAD_X = 18;
// PAD_TOP 은 첫 노드(cy = PAD_TOP)가 스크롤 영역 맨 위에 붙어도 그 위에 솟는
// 걷는 감자(WALKER, 62px, bottom-[calc(100%-6px)]) + 펄스 링이 잘리지 않도록
// 확보하는 상단 여백. current 노드 반지름(34) - 6 + 감자 높이(62) ≈ 90px 가
// cy 위로 솟으므로, 첫 노드가 current 여도 감자 top 이 컨테이너 안에 들어오게 96 으로 둔다.
const PAD_TOP = 96;
const PAD_BOTTOM = 88;
const SPACING = 120;
const COLS = [0.2, 0.5, 0.8, 0.5];

interface PositionedStation extends TrailStation {
  cx: number;
  cy: number;
}

function zigzag(stations: TrailStation[], width: number): PositionedStation[] {
  const usable = Math.max(120, width - PAD_X * 2);
  return stations.map((s, i) => ({
    ...s,
    cx: PAD_X + COLS[i % COLS.length] * usable,
    cy: PAD_TOP + i * SPACING,
  }));
}

function pathD(nodes: PositionedStation[]): string {
  if (!nodes.length) return "";
  let d = `M ${nodes[0].cx} ${nodes[0].cy}`;
  for (let i = 1; i < nodes.length; i++) {
    const p = nodes[i - 1];
    const c = nodes[i];
    const my = (p.cy + c.cy) / 2;
    d += ` C ${p.cx} ${my}, ${c.cx} ${my}, ${c.cx} ${c.cy}`;
  }
  return d;
}

interface TrailMapProps {
  onNavigate?: (href: string) => void;
}

export default function TrailMap({ onNavigate }: TrailMapProps = {}) {
  return (
    <React.Suspense
      fallback={
        <TrailMapContent
          requestedCategoryPath={null}
          searchParamsString=""
          onNavigate={onNavigate}
        />
      }
    >
      <TrailMapWithSearchParams onNavigate={onNavigate} />
    </React.Suspense>
  );
}

function TrailMapWithSearchParams({ onNavigate }: TrailMapProps) {
  const searchParams = useSearchParams();
  return (
    <TrailMapContent
      requestedCategoryPath={searchParams.get(TRAIL_QUERY_PARAM)}
      searchParamsString={searchParams.toString()}
      onNavigate={onNavigate}
    />
  );
}

function TrailMapContent({
  requestedCategoryPath,
  searchParamsString,
  onNavigate,
}: TrailMapProps & {
  requestedCategoryPath: string | null;
  searchParamsString: string;
}) {
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const { stations, ready, categoryPath } = useTrailStations(
    pathname,
    requestedCategoryPath,
  );
  const categoryOptions = React.useMemo(
    () => getTrailCategoryOptions(pathname),
    [pathname],
  );
  const onSelectCategory = React.useCallback(
    (nextCategoryPath: string) => {
      const nextSearchParams = new URLSearchParams(searchParamsString);
      const defaultCategoryPath = getTrailCategoryPath(pathname);
      if (nextCategoryPath === defaultCategoryPath) {
        nextSearchParams.delete(TRAIL_QUERY_PARAM);
      } else {
        nextSearchParams.set(TRAIL_QUERY_PARAM, nextCategoryPath);
      }
      const nextQuery = nextSearchParams.toString();
      router.push(`${pathname}${nextQuery ? `?${nextQuery}` : ""}`, {
        scroll: false,
      });
    },
    [pathname, router, searchParamsString],
  );
  return (
    <TrailScroll
      stations={stations}
      ready={ready}
      categoryPath={categoryPath}
      categoryOptions={categoryOptions}
      onSelectCategory={onSelectCategory}
      onNavigate={onNavigate}
    />
  );
}

/**
 * 출석 집합으로부터 "현재 위치(감자)" 없는 지역 목록을 만든다 (공부 진행상황 과목별 지도, JCG-2163).
 *
 * 셸의 {@link useTrailStations} 는 현재 경로를 "current" 로 잡아 감자를 세우지만, 진행상황 화면은
 * 특정 글에 머무는 맥락이 아니므로 current 를 두지 않는다. 출석한 지역 → done, 그 외 → locked,
 * 처음부터 첫 미완료 하나만 next(점선) 로 표시한다.
 *
 * @param categoryPath `/coding/c-language` · `/theory/db` 등 과목 카테고리 경로
 * @param attendedSlugs 출석한 slug 집합(앞 슬래시 없음 — API attendedPages.slug 와 동일 형식)
 * @param reviewCounts slug(앞 슬래시 없음) → 회독 수 Map. 주어지면 각 지역 노드에 "n회독"
 *   뱃지를 표기한다(JCG-2348). 미지정 시 출석=1 / 미출석=0 으로 폴백(뱃지는 >=1 일 때만 노출).
 */
export function buildAttendedStations(
  categoryPath: string,
  attendedSlugs: Set<string>,
  reviewCounts?: Map<string, number>,
): TrailStation[] {
  const defs = buildTrailStations(categoryPath);
  const base: TrailStation[] = defs.map((d) => {
    const isAttended = attendedSlugs.has(d.href.slice(1));
    return {
      ...d,
      status: isAttended ? "done" : "locked",
      attended: isAttended,
      // 회독 수: reviewCounts 가 있으면 그 값, 없으면 출석=1/미출석=0 폴백. ZigNode 의 "n회독"
      // 뱃지는 reviewCount>=1 일 때만 렌더되므로 미출석(0)은 뱃지 미표시. (JCG-2348)
      reviewCount: reviewCounts?.get(d.href.slice(1)) ?? (isAttended ? 1 : 0),
    };
  });
  for (let i = 0; i < base.length; i++) {
    if (base[i].status === "locked") {
      base[i] = { ...base[i], status: "next" };
      break;
    }
  }
  return base;
}

/**
 * 공부 진행상황 — 특정 과목 전체 오솔길 + 출석 상태 (JCG-2163).
 *
 * 셸 좌측 사이드 {@link TrailMap} 과 같은 비주얼(오솔길·지역 노드)을 재활용하되, "현재 위치(감자)"는
 * 두지 않고 출석 완료/미방문만 표시한다. 출석 데이터는 부모(StudyProgressPage)가 이미 받은
 * `/api/study-progress` 의 attendedPages 를 합친 집합을 props 로 받아 추가 fetch 가 없다.
 * `reviewCounts`(slug→회독 수)도 같은 응답에서 부모가 구성해 넘기므로, 각 지역 노드에 "n회독"
 * 뱃지가 함께 표시된다(JCG-2348). 셸 밖(/my)에서 쓰이므로 ArticleShellContext(attendanceVersion)
 * 에 의존하지 않는다.
 */
export function SubjectProgressTrail({
  categoryPath,
  attendedSlugs,
  reviewCounts,
}: {
  categoryPath: string;
  attendedSlugs: Set<string>;
  /** slug → 회독 수 Map. 주어지면 각 지역 노드에 "n회독" 뱃지를 표기한다(JCG-2348). */
  reviewCounts?: Map<string, number>;
}) {
  const stations = React.useMemo(
    () => buildAttendedStations(categoryPath, attendedSlugs, reviewCounts),
    [categoryPath, attendedSlugs, reviewCounts],
  );
  // 진행상황 화면은 가로로 넓으니 좌우를 끝까지 쓰는 구불구불 뱀길로 펼친다.
  return (
    <TrailScroll
      stations={stations}
      ready
      layout="serpentine"
      categoryPath={categoryPath}
    />
  );
}

/* ── 공유 비주얼: 진행 헤더 + 스크롤 오솔길 (셸/진행상황 공용) ── */
function TrailScroll({
  stations,
  ready,
  categoryPath,
  categoryOptions = [],
  onSelectCategory,
  onNavigate,
  layout = "vertical",
}: {
  stations: TrailStation[];
  ready: boolean;
  categoryPath?: string | null;
  categoryOptions?: TrailCategoryOption[];
  onSelectCategory?: (categoryPath: string) => void;
  /** 길 배치 — 셸 사이드(좁음)는 세로 지그재그, 진행상황(넓음)은 좌우로 펼치는 구불구불 뱀길. */
  layout?: "vertical" | "serpentine";
  onNavigate?: (href: string) => void;
}) {
  const isSerpentine = layout === "serpentine";
  const router = useRouter();
  const [ref, width] = useMeasure();

  // 진행도: 출석한 정거장 수(현재 정거장도 출석했으면 포함).
  const doneCount = stations.filter((s) => s.attended).length;
  const total = stations.length;
  const showCategorySwitch =
    !isSerpentine &&
    categoryOptions.length > 1 &&
    typeof onSelectCategory === "function";

  // 현재 지역(없으면 마지막 done)이 스크롤 뷰포트 가운데 오도록 정렬.
  // 지역 cy = PAD_TOP + idx * SPACING (zigzag 좌표와 동일).
  const currentIdx = React.useMemo(() => {
    const cur = stations.findIndex((s) => s.status === "current");
    if (cur >= 0) return cur;
    for (let i = stations.length - 1; i >= 0; i--) {
      if (stations[i].status === "done") return i;
    }
    return 0;
  }, [stations]);

  React.useEffect(() => {
    // 뱀길(진행상황)은 내부 세로 스크롤이 없어 자동 정렬이 불필요하다.
    if (isSerpentine) return;
    const el = ref.current;
    if (!el || !ready || !total) return;
    const targetY = PAD_TOP + currentIdx * SPACING;
    // 컨테이너 중앙에 지역이 오도록. clamp 은 브라우저가 자동 처리(음수/초과 무시).
    el.scrollTop = targetY - el.clientHeight / 2;
  }, [ref, ready, total, currentIdx, isSerpentine]);

  const onSelect = React.useCallback(
    (href: string) => {
      const targetHref = getTrailHref(href, categoryPath);
      if (onNavigate) onNavigate(targetHref);
      else router.push(targetHref);
    },
    [categoryPath, onNavigate, router],
  );

  if (!total) {
    return (
      <p className="px-1 py-6 text-center text-[12.5px] text-muted-foreground">
        이 카테고리의 학습 경로를 준비 중이에요.
      </p>
    );
  }

  return (
    <div className={isSerpentine ? "flex flex-col" : "flex min-h-0 flex-1 flex-col"}>
      {/* 진행 헤더 */}
      <div className="px-1 pb-3 pt-0.5">
        {showCategorySwitch && onSelectCategory && (
          <TrailCategorySwitch
            options={categoryOptions}
            selectedCategoryPath={categoryPath}
            onSelect={onSelectCategory}
          />
        )}
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[12.5px] font-bold text-foreground">
            지역 진행 <span className="text-amber-600 dark:text-amber-400">{doneCount}</span>
            <span className="font-semibold text-muted-foreground"> / {total} 지역</span>
          </span>
        </div>
        <div className="h-[7px] overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-[width] duration-500"
            style={{ width: `${(doneCount / total) * 100}%` }}
          />
        </div>
      </div>

      {/* 스크롤 맵 — 세로(셸)는 내부 스크롤, 뱀길(진행상황)은 자연 높이로 펼침 */}
      <div
        ref={ref}
        className={cn(
          "trail-map-bg -mx-1.5 overflow-x-hidden px-1.5",
          isSerpentine ? "" : "min-h-0 flex-1 overflow-y-auto",
        )}
      >
        <ZigMap
          stations={stations}
          width={width}
          onSelect={onSelect}
          layout={layout}
        />
      </div>
    </div>
  );
}

function shortCategoryLabel(name: string): string {
  switch (name) {
    case "공통 기초":
      return "공통";
    case "C언어":
      return "C";
    default:
      return name;
  }
}

function TrailCategorySwitch({
  options,
  selectedCategoryPath,
  onSelect,
}: {
  options: TrailCategoryOption[];
  selectedCategoryPath?: string | null;
  onSelect: (categoryPath: string) => void;
}) {
  return (
    <div
      role="group"
      aria-label="학습 지도 선택"
      className="mb-2 grid grid-flow-col auto-cols-fr gap-1 rounded-lg border border-border bg-muted p-1"
    >
      {options.map((option) => {
        const active = option.href === selectedCategoryPath;
        return (
          <button
            key={option.href}
            type="button"
            aria-pressed={active}
            aria-label={`${option.name} 지도 보기`}
            onClick={() => {
              if (!active) onSelect(option.href);
            }}
            className={cn(
              "min-w-0 rounded-md px-2 py-1 text-[11px] font-bold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              active
                ? "bg-amber-500 text-white shadow-sm dark:bg-amber-400 dark:text-amber-950"
                : "text-muted-foreground hover:bg-background hover:text-foreground",
            )}
          >
            <span className="block truncate">
              {shortCategoryLabel(option.name)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── 좌우로 펼치는 구불구불 길(뱀길 / boustrophedon) — 넓은 공부 진행상황용 (JCG-2163) ──
 * 좌→우로 한 줄 채우고 같은 x 에서 한 칸 내려가 우→좌, 다시 내려가 좌→우 … 식으로 가로 폭을
 * 끝까지 쓰며 구불구불 내려간다. 좁은 셸 사이드의 세로 지그재그(zigzag)와 좌표 규칙만 다르고
 * 노드/길 비주얼은 동일하게 재활용한다. */
// 첫 행 노드(cy = SERP_PAD_TOP)는 div 전체(원형 노드 + 라벨 pill + "n회독" 뱃지)를
// cy 에 translate(-50%,-50%) 로 수직 중앙 정렬하므로, 원형 노드 윗변 = cy - divHeight/2 다.
// done 노드(56) + 라벨(mt-1.5 + pill 최대 3줄) + 회독 뱃지(JCG-2348)까지 더하면 divHeight 가
// ~106~142px 라 divHeight/2 ≈ 53~71px > 48 이 되어 첫 행 원형 노드 상단이 컨테이너 위로
// 잘렸다(serpentine 컨테이너는 overflow-x-hidden 이라 overflow-y 가 auto 로 강제돼 y<0 이 클립됨).
// 회독 뱃지 유무·라벨 줄 수와 무관하게 안 잘리도록 셸 vertical 의 PAD_TOP(96)에 준해 92 로 키운다.
// (이 값은 cy 계산과 totalH 계산 두 곳에 함께 쓰여, 키우면 전체가 아래로 내려가며 상단 여백이 생긴다.)
const SERP_PAD_TOP = 92;
const SERP_PAD_BOTTOM = 96; // 줄바꿈 라벨(최대 3줄)이 아래로 잘리지 않게 여유
const SERP_HSTEP = 150; // 노드 가로 간격 목표(라벨 겹침 방지용 하한 기준)
const SERP_VSTEP = 150; // 행 간 세로 간격

/** 측정 폭으로 한 줄에 놓을 노드 수(2~8)를 정한다. */
function serpentineCols(usable: number): number {
  return Math.max(2, Math.min(8, Math.round(usable / SERP_HSTEP) + 1));
}

/** 보행 순서(좌→우→내려감→우→좌 …)대로 격자 좌표를 배치. originX·step 은 라벨이
 *  컨테이너 밖으로 잘리지 않게 라벨 절반폭만큼 안쪽으로 들인 값을 받는다. */
function serpentine(
  stations: TrailStation[],
  originX: number,
  step: number,
  cols: number,
): PositionedStation[] {
  return stations.map((s, i) => {
    const row = Math.floor(i / cols);
    const idxInRow = i % cols;
    // 짝수 행은 좌→우, 홀수 행은 우→좌 → 행 끝에서 같은 x 로 내려가는 자연스러운 뱀길
    const col = row % 2 === 0 ? idxInRow : cols - 1 - idxInRow;
    return {
      ...s,
      cx: originX + col * step,
      cy: SERP_PAD_TOP + row * SERP_VSTEP,
    };
  });
}

/** 뱀길 경로 — 가로 구간은 완만한 곡선, 행 전환(같은 x 로 내려감)은 바깥으로 둥글게 부푼 U턴. */
function serpentinePathD(
  nodes: PositionedStation[],
  centerX: number,
  turn: number,
): string {
  if (!nodes.length) return "";
  let d = `M ${nodes[0].cx} ${nodes[0].cy}`;
  for (let i = 1; i < nodes.length; i++) {
    const p = nodes[i - 1];
    const c = nodes[i];
    if (p.cy === c.cy) {
      const mx = (p.cx + c.cx) / 2;
      d += ` C ${mx} ${p.cy}, ${mx} ${c.cy}, ${c.cx} ${c.cy}`;
    } else {
      // 우측 끝이면 오른쪽으로, 좌측 끝이면 왼쪽으로 부풀려 매끈한 반환점(U턴)을 만든다.
      const bx = p.cx + (p.cx > centerX ? 1 : -1) * turn;
      d += ` C ${bx} ${p.cy}, ${bx} ${c.cy}, ${c.cx} ${c.cy}`;
    }
  }
  return d;
}

/* ── 길 맵 (세로 지그재그 / 가로 뱀길 공용) ── */
function ZigMap({
  stations,
  width,
  onSelect,
  layout = "vertical",
}: {
  stations: TrailStation[];
  width: number;
  onSelect: (href: string) => void;
  layout?: "vertical" | "serpentine";
}) {
  const isSerpentine = layout === "serpentine";
  const rawUsable = Math.max(120, width - PAD_X * 2);
  const cols = isSerpentine ? serpentineCols(rawUsable) : 1;

  // 뱀길: 라벨이 가로로 나란히 놓이므로 칼럼 슬롯 폭에 맞춰 라벨을 감싸고(겹침 방지),
  // 양 끝 라벨이 컨테이너 밖으로 잘리지 않게 라벨 절반폭만큼 안쪽으로 들여 배치한다.
  const nodeWidth = isSerpentine
    ? Math.min(150, Math.max(84, rawUsable / cols - 14))
    : 96;
  const innerSpan = Math.max(0, rawUsable - nodeWidth);
  const step = cols > 1 ? innerSpan / (cols - 1) : 0;
  const originX = PAD_X + nodeWidth / 2;
  const centerX = originX + ((cols - 1) * step) / 2;
  const turn = Math.min(44, Math.max(24, step * 0.45));

  const nodes = isSerpentine
    ? serpentine(stations, originX, step, cols)
    : zigzag(stations, width);

  const totalH = isSerpentine
    ? SERP_PAD_TOP +
      Math.max(0, Math.ceil(stations.length / cols) - 1) * SERP_VSTEP +
      SERP_PAD_BOTTOM
    : nodes.length
      ? nodes[nodes.length - 1].cy + PAD_BOTTOM
      : 200;

  const makePath = (ns: PositionedStation[]) =>
    isSerpentine ? serpentinePathD(ns, centerX, turn) : pathD(ns);

  const progressIdx = lastContinuousAttendedIndex(nodes);
  const doneNodes = progressIdx >= 0 ? nodes.slice(0, progressIdx + 1) : [];
  const futureNodes = nodes.slice(progressIdx >= 0 ? progressIdx : 0);
  const fullD = makePath(nodes);

  return (
    <div className="relative w-full" style={{ height: totalH }}>
      <svg
        width="100%"
        height={totalH}
        className="pointer-events-none absolute inset-0"
      >
        {/* 흙길 본체 */}
        <path d={fullD} fill="none" stroke="#cdb38c" strokeWidth={20} strokeLinecap="round" strokeLinejoin="round" />
        <path d={fullD} fill="none" stroke="#e6d3ad" strokeWidth={13} strokeLinecap="round" strokeLinejoin="round" />
        {/* 걸어온 길 */}
        <path d={makePath(doneNodes)} fill="none" stroke="#f59e0b" strokeWidth={6} strokeLinecap="round" strokeDasharray="1 13" opacity={0.85} />
        {/* 앞으로 갈 길(발자국) */}
        <path d={makePath(futureNodes)} fill="none" stroke="#a98c5f" strokeWidth={4} strokeLinecap="round" strokeDasharray="2 12" opacity={0.55} />
      </svg>

      {nodes.map((n) => (
        <ZigNode
          key={n.href}
          n={n}
          onSelect={onSelect}
          nodeWidth={nodeWidth}
          wrapLabel={isSerpentine}
        />
      ))}
    </div>
  );
}

function nodeBodyClass(status: StationStatus): string {
  switch (status) {
    case "done":
      return "bg-amber-100 dark:bg-amber-500/15 border-[3px] border-amber-500 text-amber-700 dark:text-amber-300";
    case "current":
      return "bg-card border-[3px] border-amber-500";
    case "next":
      return "bg-card border-[3px] border-dashed border-amber-400 text-amber-600 dark:text-amber-400";
    default:
      return "bg-muted border-[3px] border-dashed border-border text-muted-foreground";
  }
}

function ZigNode({
  n,
  onSelect,
  nodeWidth = 96,
  wrapLabel = false,
}: {
  n: PositionedStation;
  onSelect: (href: string) => void;
  /** 노드 컬럼 폭(px). 뱀길은 step 기반, 세로 지그재그는 96(w-24). */
  nodeWidth?: number;
  /** 라벨을 폭에 맞춰 줄바꿈할지(뱀길은 가로로 나란히 놓여 겹치므로 true). */
  wrapLabel?: boolean;
}) {
  const st = n.status;
  const size = st === "current" ? 68 : st === "locked" ? 48 : 56;

  // 글 아이콘: 폴더 그리드(PostIcon)와 동일한 폴백 패턴 — 개별 글 이미지 로드
  // 실패 시 scope 대표 이미지로 교체.
  const [imgSrc, setImgSrc] = React.useState(n.iconUrl);
  // 현재·출석완료(done) 지역은 컬러, 아직 안 간 곳(next/locked)은 그레이스케일. locked 는 더 흐리게.
  const iconTone =
    st === "current" || st === "done"
      ? ""
      : st === "locked"
        ? "grayscale opacity-60"
        : "grayscale opacity-90";
  // 테두리(border-[3px]) 안쪽으로 들어가 status별 테두리색이 보이도록 6px 줄임.
  const iconSize = size - 6;

  return (
    <div
      className="absolute flex flex-col items-center"
      style={{
        left: n.cx,
        top: n.cy,
        width: nodeWidth,
        transform: "translate(-50%, -50%)",
      }}
    >
      {/* 현재 위치: 걷는 감자 */}
      {st === "current" && (
        <div className="absolute bottom-[calc(100%-6px)] flex flex-col items-center">
          <SeasonalImage
            src={WALKER}
            alt="현재 위치 감자"
            width={62}
            height={62}
            className="trail-walk h-[62px] w-[62px] object-contain drop-shadow-[0_4px_5px_rgba(0,0,0,0.18)]"
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => onSelect(n.href)}
        aria-label={n.title}
        aria-current={st === "current" ? "page" : undefined}
        title={n.title}
        className={cn(
          "relative flex items-center justify-center rounded-full text-sm font-extrabold transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
          st === "current" && "animate-stamp",
          nodeBodyClass(st),
        )}
        style={{ width: size, height: size }}
      >
        {st === "current" && (
          <span className="trail-pulse-ring pointer-events-none absolute -inset-[3px] rounded-full border-[3px] border-amber-400" />
        )}
        <span
          className={cn(
            "relative block shrink-0 overflow-hidden rounded-full",
            iconTone,
          )}
          style={{ width: iconSize, height: iconSize }}
        >
          <Image
            src={imgSrc}
            alt=""
            fill
            className="object-cover"
            sizes={`${iconSize}px`}
            onError={() => setImgSrc(getScopeImage(n.scope))}
          />
        </span>
      </button>

      {/* 표지판 라벨 */}
      <div
        className="mt-1.5 flex flex-col items-center gap-1 text-center leading-[1.15]"
        style={{ maxWidth: nodeWidth }}
      >
        <div
          className={cn(
            // 흙길 배경과 겹쳐도 읽히도록 반투명 카드 pill + 그림자
            "rounded-md bg-card/85 px-1.5 py-0.5 text-[11.5px] shadow-sm ring-1 ring-border/60 backdrop-blur-[2px]",
            // 뱀길은 폭에 맞춰 줄바꿈(겹침 방지), 세로 지그재그는 기존처럼 한 줄.
            wrapLabel ? "max-w-full whitespace-normal break-keep" : "inline-block",
            st === "current"
              ? "font-extrabold text-amber-700 dark:text-amber-300"
              : st === "locked"
                ? "font-semibold text-muted-foreground"
                : "font-semibold text-foreground",
          )}
        >
          {n.attended && (
            <Check
              className="mr-0.5 inline-block h-[18px] w-[18px] align-middle text-emerald-600 dark:text-emerald-400"
              strokeWidth={3.5}
              aria-label="출석 완료"
            />
          )}
          {n.title}
        </div>
        {/* 회독 수 뱃지 — 출석한 지역만(>=1). "이 단계 2회 / 저 단계 0회"를 한눈에 구분(JCG-2348).
            출석(Check)과 같은 emerald 시맨틱 계열로 amber 길/노드와 충돌하지 않게 한다. */}
        {n.reviewCount >= 1 && (
          <div
            className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-100 px-1.5 py-px text-[10px] font-bold text-emerald-700 shadow-sm backdrop-blur-[2px] dark:border-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300"
            aria-label={`${n.reviewCount}회독`}
            title={`${n.reviewCount}회독`}
          >
            {n.reviewCount}회독
          </div>
        )}
        {st === "current" && (
          <div className="inline-block rounded bg-card/85 px-1 py-0.5 text-[10px] font-bold text-amber-600 shadow-sm ring-1 ring-border/60 backdrop-blur-[2px] dark:text-amber-400">
            지금 여기 📍
          </div>
        )}
      </div>
    </div>
  );
}
